import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface OrgAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface BusinessDetails {
  name: string;
  dba: string;
  orgType: string;
  ein: string;
  phone: string;
  website: string;
  disclosure: string;
  subDomain: string;
  referralCode?: string;
  address: OrgAddress;
}

export interface RepresentativeDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  dobDay: number;
  dobMonth: number;
  dobYear: number;
  address: {
    line1: string;
    city: string;
    state: string;
    postal_code: string;
  };
  ssnLast4: string;
}

export interface BrevoConfig {
  apiKey: string;
  senderName: string;
  senderEmail: string;
}

export interface BillingSetup {
  bankName: string;
  bankLast4: string;
  customerId: string;
}

export interface PartnerPrefill {
  orgName?: string;
  email?: string;
  contactName?: string;
  phone?: string;
  referralCode?: string;
}

export interface OnboardingState {
  organizationId: string | null;
  organization: any | null;
  currentStep: number;
  partnerPrefill: PartnerPrefill | null;
  businessDetails: BusinessDetails | null;
  representativeDetails: RepresentativeDetails | null;
  documentsComplete: boolean;
  uploadedDocUrls: string[];
  stripeAccountId: string | null;
  stripeStatus: string;
  brevoConfig: BrevoConfig | null;
  brevoConfigured: boolean;
  billingSetup: BillingSetup | null;
  billingActive: boolean;
  provisioningStatus: string | null;
  isComplete: boolean;
  source: 'partner' | 'marketing' | 'direct';
  referralId: string | null;
  partnerId: string | null;
  authToken: string | null;
  authVerifiedAt: number | null;
}

const INITIAL_STATE: OnboardingState = {
  organizationId: null,
  organization: null,
  currentStep: 0,
  partnerPrefill: null,
  businessDetails: null,
  representativeDetails: null,
  documentsComplete: false,
  uploadedDocUrls: [],
  stripeAccountId: null,
  stripeStatus: 'none',
  brevoConfig: null,
  brevoConfigured: false,
  billingSetup: null,
  billingActive: false,
  provisioningStatus: null,
  isComplete: false,
  source: 'direct',
  referralId: null,
  partnerId: null,
  authToken: null,
  authVerifiedAt: null,
};

@Injectable({
  providedIn: 'root'
})
export class StateManagementService {
  private _state$ = new BehaviorSubject<OnboardingState>(INITIAL_STATE);

  readonly state$ = this._state$.asObservable();
  readonly organization$ = this._state$.pipe(map(s => s.organization));
  readonly organizationId$ = this._state$.pipe(map(s => s.organizationId));
  readonly currentStep$ = this._state$.pipe(map(s => s.currentStep));
  readonly stripeStatus$ = this._state$.pipe(map(s => s.stripeStatus));
  readonly brevoConfigured$ = this._state$.pipe(map(s => s.brevoConfigured));
  readonly billingActive$ = this._state$.pipe(map(s => s.billingActive));
  readonly isComplete$ = this._state$.pipe(map(s => s.isComplete));

  get snapshot(): OnboardingState {
    return this._state$.value;
  }

  setOrganization(org: any): void {
    this._state$.next({
      ...this._state$.value,
      organizationId: org.id,
      organization: org,
      stripeAccountId: org.StripeAccountId || org.AccountId || null,
      stripeStatus: org.StripeConnectStatus || 'none',
      brevoConfigured: !!org.BrevoConfigured,
      billingActive: !!org.BillingSetupComplete,
      provisioningStatus: org.ProvisioningStatus || null,
    });
  }

  setCurrentStep(step: number): void {
    this._state$.next({ ...this._state$.value, currentStep: step });
  }

  setBusinessDetails(details: BusinessDetails): void {
    this._state$.next({ ...this._state$.value, businessDetails: details });
  }

  setRepresentativeDetails(details: RepresentativeDetails): void {
    this._state$.next({ ...this._state$.value, representativeDetails: details });
  }

  setDocumentsComplete(urls: string[]): void {
    this._state$.next({
      ...this._state$.value,
      documentsComplete: true,
      uploadedDocUrls: urls,
    });
  }

  setStripeAccount(accountId: string, status: string): void {
    this._state$.next({
      ...this._state$.value,
      stripeAccountId: accountId,
      stripeStatus: status,
      organization: this._state$.value.organization
        ? { ...this._state$.value.organization, StripeAccountId: accountId, StripeConnectStatus: status }
        : null,
    });
  }

  setStripeStatus(status: string): void {
    this._state$.next({ ...this._state$.value, stripeStatus: status });
  }

  setBrevoConfigured(config: BrevoConfig): void {
    this._state$.next({
      ...this._state$.value,
      brevoConfig: config,
      brevoConfigured: true,
    });
  }

  setBillingActive(setup: BillingSetup): void {
    this._state$.next({
      ...this._state$.value,
      billingSetup: setup,
      billingActive: true,
      organization: this._state$.value.organization
        ? { ...this._state$.value.organization, BillingSetupComplete: true }
        : null,
    });
  }

  setProvisioningStatus(status: string): void {
    this._state$.next({
      ...this._state$.value,
      provisioningStatus: status,
      organization: this._state$.value.organization
        ? { ...this._state$.value.organization, ProvisioningStatus: status }
        : null,
    });
  }

  checkAndSetComplete(): void {
    const s = this._state$.value;
    const complete = s.stripeStatus === 'active' && s.brevoConfigured;
    this._state$.next({ ...s, isComplete: complete });
  }

  reset(): void {
    this._state$.next(INITIAL_STATE);
  }

  setSource(source: 'partner' | 'marketing' | 'direct'): void {
    this._state$.next({ ...this._state$.value, source });
  }

  setReferralId(referralId: string): void {
    this._state$.next({ ...this._state$.value, referralId });
  }

  setPartnerId(partnerId: string): void {
    this._state$.next({ ...this._state$.value, partnerId });
  }

  setPartnerPrefill(prefill: PartnerPrefill): void {
    this._state$.next({ ...this._state$.value, partnerPrefill: prefill });
  }

  setAuthToken(token: string, verifiedAt?: number): void {
    this._state$.next({
      ...this._state$.value,
      authToken: token,
      authVerifiedAt: verifiedAt || Date.now(),
    });
  }

  isAuthVerified(): boolean {
    return !!this._state$.value.authVerifiedAt && !!this._state$.value.authToken;
  }
}
